import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, ArrowLeft, Check, Search, CalendarClock, AlertTriangle, Quote, Phone } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import LeadForm from "./LeadForm";
import SEO from "./SEO";
import { TRADE_PAGES, TRADE_CITIES, buildStack } from "../tradesData";
import { CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_HREF } from "../mock";

export default function TradePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const data = TRADE_PAGES[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!data) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-5 pt-24">
          <h1 className="font-display text-white text-5xl mb-4">Trade not found</h1>
          <p className="text-[#9aa6b5] mb-6">That trade page doesn't exist yet.</p>
          <button className="btn-orange" onClick={() => navigate("/")}>
            Back home
          </button>
        </div>
        <Footer />
      </>
    );
  }

  const TradeIcon = Icons[data.icon] || Icons.Hammer;
  const stack = buildStack(data.label);

  return (
    <>
      <SEO
        title={`${data.name} Marketing in Houston, TX`}
        description={data.heroDesc}
        path={`/${slug}`}
      />
      <Navbar />
      <main className="bg-ls">
        {/* Hero */}
        <section className="relative overflow-hidden pt-28 md:pt-36 pb-16 md:pb-20 grain">
          <div
            className="pointer-events-none absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-20"
            style={{ background: "radial-gradient(circle, #f26a21, transparent 70%)" }}
          />
          <div className="max-w-5xl mx-auto px-5 md:px-8 relative">
            <Link
              to="/#trades"
              className="inline-flex items-center gap-2 text-[#9aa6b5] hover:text-orange transition-colors font-cond tracking-wide mb-8"
            >
              <ArrowLeft className="w-4 h-4" /> All trades
            </Link>

            <div className="inline-flex items-center gap-2.5 border border-[rgba(242,106,33,0.4)] rounded-[4px] px-3 py-1.5 mb-6">
              <TradeIcon className="w-4 h-4 text-orange" />
              <span className="ls-eyebrow">
                {data.name} · Houston, TX
              </span>
            </div>

            <h1 className="font-display text-white text-[10vw] sm:text-5xl md:text-[64px] max-w-4xl">
              {data.heroTitle}
            </h1>

            <p className="mt-6 text-lg md:text-xl text-[#aeb8c4] max-w-3xl leading-relaxed">
              {data.heroDesc}
            </p>

            <p className="mt-5 text-sm text-[#7f8b99] font-cond tracking-wide max-w-3xl">
              {TRADE_CITIES}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="#quote" className="btn-orange text-base">
                Get More {data.name} Jobs
              </a>
              <a href={CONTACT_PHONE_HREF} className="btn-ghost text-base">
                <Phone className="w-4 h-4" /> {CONTACT_PHONE}
              </a>
            </div>
          </div>
        </section>

        {/* Where jobs leak */}
        <section className="bg-ls-alt py-16 md:py-24 border-t" style={{ borderColor: "var(--ls-border)" }}>
          <div className="max-w-5xl mx-auto px-5 md:px-8">
            <p className="ls-eyebrow mb-4">The honest version</p>
            <h2 className="font-display text-white text-[9vw] sm:text-4xl md:text-5xl mb-10">
              Where {data.label} jobs actually leak.
            </h2>
            <div className="grid md:grid-cols-3 gap-5">
              {data.leaks.map((leak, i) => (
                <div key={i} className="card-dark p-6">
                  <AlertTriangle className="w-6 h-6 text-orange mb-4" />
                  <p className="text-[#e4e8ee] leading-relaxed">{leak}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 text-lg text-[#9aa6b5] max-w-3xl">
              None of that is a work problem. It's a phone-and-follow-up problem. We fix it in a week.
            </p>
          </div>
        </section>

        {/* Services + Google + Season */}
        <section className="bg-ls py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-5 md:px-8">
            <p className="ls-eyebrow mb-4">{data.name} services we route to you</p>
            <h2 className="font-display text-white text-[9vw] sm:text-4xl md:text-5xl mb-4">
              The {data.label} work booked around Houston.
            </h2>
            <p className="text-lg text-[#9aa6b5] max-w-3xl mb-10">
              Your site, AI, and follow-up are tuned to the exact jobs coastal Texas homeowners actually search for.
            </p>

            <div className="grid md:grid-cols-2 gap-x-10 gap-y-4 mb-14">
              {data.services.map((s) => (
                <div key={s} className="flex items-center gap-3">
                  <span
                    className="flex items-center justify-center w-5 h-5 rounded-full shrink-0"
                    style={{ backgroundColor: "var(--ls-orange)" }}
                  >
                    <Check className="w-3.5 h-3.5" stroke="#0a0f16" strokeWidth={3} />
                  </span>
                  <span className="text-[#e4e8ee]">{s}</span>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="card-dark p-7">
                <div className="flex items-center gap-2 mb-4">
                  <Search className="w-5 h-5 text-orange" />
                  <h3 className="font-cond font-bold text-xl text-white uppercase tracking-wide">
                    What homeowners actually Google
                  </h3>
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {data.googles.map((g) => (
                    <span
                      key={g}
                      className="px-3 py-1.5 rounded-full text-sm text-[#c7d0db]"
                      style={{ background: "#05070b", border: "1px solid var(--ls-border)" }}
                    >
                      &ldquo;{g}&rdquo;
                    </span>
                  ))}
                </div>
                <p className="mt-4 text-[#9aa6b5] leading-relaxed">
                  Your site targets these terms in your service area — not vague national keywords, but the searches
                  your neighbors are typing right now.
                </p>
              </div>

              <div className="card-dark p-7">
                <div className="flex items-center gap-2 mb-4">
                  <CalendarClock className="w-5 h-5 text-orange" />
                  <h3 className="font-cond font-bold text-xl text-white uppercase tracking-wide">
                    Your season, mapped
                  </h3>
                </div>
                <p className="text-[#9aa6b5] leading-relaxed">{data.season}</p>
                <p className="mt-3 text-[#9aa6b5] leading-relaxed">
                  We pre-load campaigns and follow-ups around your calendar, not a generic playbook.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Stack applied */}
        <section className="bg-ls-alt py-16 md:py-24 border-t" style={{ borderColor: "var(--ls-border)" }}>
          <div className="max-w-6xl mx-auto px-5 md:px-8">
            <p className="ls-eyebrow mb-4">How {data.label} jobs get booked</p>
            <h2 className="font-display text-white text-[9vw] sm:text-4xl md:text-5xl mb-10">
              The stack, applied to your trade.
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {stack.map((item, i) => (
                <div key={i} className="card-dark p-6">
                  <span className="big-index text-3xl">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-cond font-extrabold text-xl text-white uppercase tracking-wide mt-3">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-[#9aa6b5] leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Result */}
        <section className="bg-ls py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-5 md:px-8">
            <p className="ls-eyebrow mb-4">What it did for a {data.label} pro</p>
            <h2 className="font-display text-white text-[9vw] sm:text-4xl md:text-5xl mb-8">
              Actual result, not a dashboard number.
            </h2>
            <div
              className="rounded-lg p-8 relative"
              style={{ background: "var(--ls-surface)", border: "1px solid rgba(242,106,33,0.5)" }}
            >
              <Quote className="w-8 h-8 text-orange mb-4" />
              <p className="text-xl md:text-2xl text-white leading-relaxed font-medium">{data.result}</p>
            </div>
          </div>
        </section>

        {/* Lead capture */}
        <section id="quote" className="bg-ls-alt py-16 md:py-24 border-t" style={{ borderColor: "var(--ls-border)" }}>
          <div className="max-w-3xl mx-auto px-5 md:px-8">
            <p className="ls-eyebrow mb-4 text-center">Get more {data.label} jobs</p>
            <h2 className="font-display text-white text-[9vw] sm:text-4xl md:text-5xl mb-3 text-center">
              Tell me about your {data.label} business.
            </h2>
            <p className="text-[#9aa6b5] text-center mb-8">
              I'll build your {data.name} site for free and wire up the system that books the jobs. Or just call{" "}
              <a href={CONTACT_PHONE_HREF} className="text-orange font-semibold">
                {CONTACT_PHONE}
              </a>
              .
            </p>
            <LeadForm defaultTrade={data.name} source={`trade_${slug}`} />
          </div>
        </section>

        {/* CTA */}
        <section className="relative overflow-hidden py-20 md:py-28" style={{ background: "var(--ls-orange)" }}>
          <div
            className="pointer-events-none absolute inset-0 opacity-10"
            style={{ backgroundImage: "radial-gradient(rgba(0,0,0,0.5) 1px, transparent 1px)", backgroundSize: "6px 6px" }}
          />
          <div className="max-w-3xl mx-auto px-5 md:px-8 text-center relative">
            <h2 className="font-display text-[#1a0f06] text-[9vw] sm:text-5xl md:text-6xl">
              Website built for free. $597/mo. Cancel anytime.
            </h2>
            <p className="mt-5 text-[#3a2410] text-lg font-semibold">
              One person doing the work. Small roster. You text me, I make the change — usually same day. You own
              everything from day one.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <a
                href={CONTACT_PHONE_HREF}
                className="inline-flex items-center gap-2 bg-[#0a0f16] text-white font-cond font-bold uppercase tracking-wide px-7 py-3.5 rounded-[4px] transition-transform hover:-translate-y-0.5"
              >
                <Phone className="w-4 h-4" /> Get My {data.name} System
              </a>
              <Link
                to="/#trades"
                className="inline-flex items-center gap-2 border border-[#1a0f06] text-[#1a0f06] font-cond font-bold uppercase tracking-wide px-7 py-3.5 rounded-[4px] transition-colors hover:bg-[#1a0f06]/10"
              >
                See other trades <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
